import type { Dispatch, SetStateAction } from 'react';
import {
    Box,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Grid
} from '@mui/material';
import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {useTranslation} from "react-i18next";
import {Measurement, TemplateData} from "../../pages/app/types.ts";

interface MeasurementListProps {
    template: TemplateData;
    setTemplate: Dispatch<SetStateAction<TemplateData>>;
    mode: string;
    pointId: number;
}

function MeasurementList({ mode, template, setTemplate, pointId }: MeasurementListProps) {
    const { t } = useTranslation();
    const [measurementInputValue, setMeasurementInputValue] = useState('');
    const [measurementColor, setMeasurementColor] = useState('#000000');

    const addMeasurement = () => {
        if (measurementInputValue.trim() !== '') {
            const point = template.measurementPoints.find(p => p.id === pointId);
            const nextId = point ? point.measurements.length + 1 : 1;

            const newMeasurement: Measurement = {
                id: `${pointId}-${nextId}`,
                label: measurementInputValue,
                color: measurementColor,
                data: {
                    messDaten: [0, 0, 0, 0, 0],
                    fd: 0,
                    td: 0,
                    fs: 0,
                    fe: 0,
                    assessment: 0,
                },
            };

            const updatedPoints = template.measurementPoints.map(point => {
                if (point.id === pointId) {
                    return {
                        ...point,
                        measurements: [...point.measurements, newMeasurement],
                    };
                }
                return point;
            });
            setTemplate((prev: TemplateData) => ({ ...prev, measurementPoints: updatedPoints }));
            setMeasurementInputValue('');
            setMeasurementColor('#000000');
        } else {
            alert(t('stepper.step.measurementPoint.measurement.addError'));
        }
    };


    const deleteMeasurement = (measurementId: string) => {
        const updatedPoints = template.measurementPoints.map(point => {
            if (point.id === pointId) {
                const filtered = point.measurements.filter(m => m.id !== measurementId);
                // IDs neu durchnummerieren ab 1
                const reindexed = filtered.map((m, index) => ({
                    ...m,
                    id: `${pointId}-${index + 1}`
                }));
                return {
                    ...point,
                    measurements: reindexed
                };
            }
            return point;
        });
        setTemplate((prev: TemplateData) => ({ ...prev, measurementPoints: updatedPoints }));
    };


    const renderMeasurement = (measurement: Measurement) => {
        return (
            <ListItem key={measurement.id} sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                    <ListItemText primary={measurement.label} />
                </Box>
                <Box sx={{ width: 24, height: 24, bgcolor: measurement.color, borderRadius: '50%', mr: 2 }} />
                {mode != 'overview' && (
                    <IconButton edge="end" aria-label="delete" onClick={() => deleteMeasurement(measurement.id)}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </ListItem>
        );
    };

    return (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <List component="div" disablePadding>
                {template.measurementPoints.find(point => point.id === pointId)?.measurements.map(measurement =>
                    renderMeasurement(measurement)
                )}
            </List>
            {mode != 'overview' && (
                <Grid container sx={{ width: '100%' }} spacing={2} alignItems="center" justifyContent="center">
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            required
                            value={measurementInputValue}
                            onChange={e => setMeasurementInputValue(e.target.value)}
                            placeholder={t('stepper.step.measurementPoint.measurement.placeholder')}
                            margin="normal"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            type="color"
                            value={measurementColor}
                            onChange={e => setMeasurementColor(e.target.value)}
                            margin="normal"
                            sx={{ minWidth: '60px', width: '100%' }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }}>
                        <IconButton onClick={addMeasurement} style={{ color: '#53b2ef' }} sx={{ height: '100%' }}>
                            <AddCircleIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}

export default MeasurementList;

